// src/client/components/settings/ApplicationSettingsTab.tsx
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import { Copy } from 'lucide-react';
import { DataTable, TableColumn } from '@client/components/shared/DataTable';
import { Setting } from '@client/types/settings';
import { useToast } from '@client/hooks/use-toast';

interface ApplicationSettingsTabProps {
  settings: Setting[];
  renderValueInput: (setting: Setting) => React.ReactNode;
  deleteSetting: (key: string) => Promise<void>;
}

export default function ApplicationSettingsTab({ 
  settings, 
  renderValueInput, 
  deleteSetting 
}: ApplicationSettingsTabProps) {
  const { toast } = useToast();
  const appSettings = settings.filter(s => s.key.startsWith('app.') || s.key.startsWith('ui.') || s.key.startsWith('features.'));

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    toast({
      title: 'Kopiert',
      description: 'Wert wurde in die Zwischenablage kopiert.',
    });
  };

  const columns: TableColumn<Setting>[] = [
    {
      key: 'key',
      header: 'Key',
      className: 'font-mono text-sm',
      render: (setting) => setting.key,
    },
    {
      key: 'value',
      header: 'Value',
      className: 'max-w-xs',
      render: (setting) => renderValueInput(setting),
    },
    {
      key: 'type',
      header: 'Type',
      render: (setting) => (
        <Badge variant="outline">{setting.type}</Badge>  // Kommt direkt aus DB
      ),
    },
    {
      key: 'updatedAt',
      header: 'Last Updated',
      className: 'text-sm text-muted-foreground',
      render: (setting) => 
        setting.updatedAt ? new Date(setting.updatedAt).toLocaleString() : '-',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Application Settings
        </CardTitle>
        <CardDescription>General application and UI configuration</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          data={appSettings}
          columns={columns}
          getRowKey={(setting) => setting.id}
          emptyState={{
            title: 'Keine Application Settings gefunden',
            description: 'Erstellen Sie Settings mit den PrÃ¤fixen app.* oder ui.*',
          }}
          actions={{
            onDelete: (setting) => deleteSetting(setting.key),
            custom: [
              {
                icon: <Copy className="h-4 w-4" />,
                label: 'Kopieren',
                onClick: (setting) => handleCopy(setting.value),
                variant: 'ghost',
              },
            ],
          }}
          deleteConfirmation={{
            title: 'Setting lÃ¶schen?',
            description: (setting) =>
              `Sind Sie sicher, dass Sie das Setting "${setting.key}" lÃ¶schen mÃ¶chten? Diese Aktion kann nicht rÃ¼ckgÃ¤ngig gemacht werden.`,
          }}
        />
      </CardContent>
    </Card>
  );
}